import { Link } from "react-router-dom";
import { useLanguage, type Language } from "../contexts/LanguageContext";
import { LANGUAGE_ROUTES } from "../utils/routes";
import { MEINHOTEL_APP, MEINHOTEL_PATHS, getMeinHotelContent } from "../data/meinhotel";

const TFV_ATTRIBUTION: Record<Language, { before: string; after: string }> = {
  tr: { before: "Fures Tech, ", after: "'ın bir projesidir." },
  en: { before: "Fures Tech is a project of ", after: "." },
  de: { before: "Fures Tech ist ein Projekt von ", after: "." },
  ru: { before: "Fures Tech — проект ", after: "." },
};

export function Footer() {
  const { t, language } = useLanguage();
  const logoSrc = "/images/fures.png";
  const r = LANGUAGE_ROUTES[language];
  const meinHotel = getMeinHotelContent(language);
  const tfvAttribution = TFV_ATTRIBUTION[language];

  const footerLinks = [
    { title: t('nav.about'), href: r.about },
    { title: t('nav.services'), href: r.services },
    { title: t('nav.projects'), href: r.projects },
    { title: 'MeinHotel PMS', href: MEINHOTEL_PATHS[language] },
    { title: t('nav.campaigns'), href: r.campaigns },
    { title: t('nav.blog'), href: r.blog },
    { title: t('nav.team') || 'Team', href: r.team },
    { title: 'FAQ', href: r.faq },
    { title: t('nav.contact'), href: r.contact },
  ];

  const legalLinks = [
    { title: t('footer.privacy'), href: r.privacy },
    { title: t('footer.cookies'), href: r.cookies },
    { title: t('footer.kvkk'), href: r.legal },
    { title: t('footer.imprint'), href: r.imprint },
  ];

  return (
    <footer className="relative bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Top Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">
          {/* Logo */}
          <div>
            <Link to="/">
              <img
                src={logoSrc}
                alt="Fures Tech"
                className="h-10 w-auto transition-transform hover:scale-110 duration-300"
              />
            </Link>
          </div>

          {/* Links with Hover Glow */}
          <nav className="flex flex-wrap gap-6">
            {footerLinks.map((link, index) => (
              <Link
                key={index}
                to={link.href}
                className="relative text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
              >
                {link.title}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-purple-600 group-hover:w-full transition-all duration-300 rounded-full"></span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Divider with Gradient */}
        <div className="mb-8 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Copyright */}
          <div className="text-gray-400 text-sm text-center md:text-left">
            <p>{t('footer.copyright').replace('{year}', String(new Date().getFullYear()))}</p>
            <p
              className="mt-3 max-w-sm leading-relaxed"
              style={{ color: "#DC0000", fontFamily: '"Inter", system-ui, sans-serif', fontSize: "13.5px" }}
            >
              {tfvAttribution.before}
              <a
                href="https://tourismusverband.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline-offset-2 hover:underline"
                style={{ color: "inherit" }}
              >
                Tourismus Förder Verband
              </a>
              {tfvAttribution.after}
            </p>
          </div>

          {/* PMS access */}
          <a
            href={MEINHOTEL_APP.loginUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-400 transition-colors hover:text-orange-400"
          >
            {meinHotel.ctaLogin} ↗
          </a>

          {/* Legal Links */}
          <div className="flex gap-6 text-sm">
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-gray-400 hover:text-orange-400 transition-colors"
              >
                {link.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
